import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BRANCH_HEADER } from "@shared/branches";
import type { BranchSummary } from "@shared/branches";
import {
  apiFetch,
  getStoredBranchId,
  setStoredBranchId,
  clearStoredBranchId,
} from "@/lib/api-fetch";
import {
  setStoredBranchDetail,
  clearStoredBranchDetail,
  RESTAURANT_RECEIPT_BRAND,
} from "@/lib/restaurant-brand";
import { useAuth } from "@/hooks/use-auth";

type BranchContextValue = {
  branches: BranchSummary[];
  activeBranch: BranchSummary | null;
  activeBranchId: number | null;
  setActiveBranchId: (id: number) => void;
  hasMultipleBranches: boolean;
  reloadBranches: () => Promise<void>;
  receiptBrand: {
    name: string;
    address: string;
    contact: string;
    poweredBy: string;
  };
};

const BranchContext = createContext<BranchContextValue | null>(null);

export function BranchProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [activeBranchId, setActiveBranchIdState] = useState<number | null>(
    () => getStoredBranchId(),
  );

  const reloadBranches = useCallback(async () => {
    const res = await apiFetch("/api/branches");
    if (!res.ok) return;
    const list = (await res.json()) as BranchSummary[];
    setBranches(list);
    if (
      activeBranchId != null &&
      !list.some((b) => b.id === activeBranchId) &&
      list[0]
    ) {
      setActiveBranchIdState(list[0].id);
      setStoredBranchId(list[0].id);
      setStoredBranchDetail(list[0]);
    }
  }, [activeBranchId]);

  useEffect(() => {
    if (!user) {
      setBranches([]);
      setActiveBranchIdState(null);
      clearStoredBranchId();
      clearStoredBranchDetail();
      return;
    }

    let cancelled = false;
    (async () => {
      const res = await apiFetch("/api/branches");
      if (!res.ok || cancelled) return;
      const list = (await res.json()) as BranchSummary[];
      if (cancelled) return;
      setBranches(list);

      const stored = getStoredBranchId();
      const validStored = stored && list.some((b) => b.id === stored);
      const nextId = validStored ? stored! : list[0]?.id ?? null;
      if (nextId != null) {
        setActiveBranchIdState(nextId);
        setStoredBranchId(nextId);
        const branch = list.find((b) => b.id === nextId);
        if (branch) setStoredBranchDetail(branch);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const setActiveBranchId = useCallback(
    (id: number) => {
      setStoredBranchId(id);
      const branch = branches.find((b) => b.id === id);
      if (branch) setStoredBranchDetail(branch);
      setActiveBranchIdState(id);
      void queryClient.resetQueries();
    },
    [queryClient, branches],
  );

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      const headers = new Headers(init?.headers);
      const branchId = getStoredBranchId();
      if (branchId != null) {
        headers.set(BRANCH_HEADER, String(branchId));
      }
      return originalFetch(input, {
        ...init,
        headers,
        credentials: init?.credentials ?? "include",
      });
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const activeBranch = useMemo(
    () => branches.find((b) => b.id === activeBranchId) ?? null,
    [branches, activeBranchId],
  );

  const receiptBrand = useMemo(
    () => ({
      name: RESTAURANT_RECEIPT_BRAND.name,
      address: RESTAURANT_RECEIPT_BRAND.address,
      contact: RESTAURANT_RECEIPT_BRAND.contact,
      poweredBy: RESTAURANT_RECEIPT_BRAND.poweredBy,
    }),
    [],
  );

  const value = useMemo<BranchContextValue>(
    () => ({
      branches,
      activeBranch,
      activeBranchId,
      setActiveBranchId,
      hasMultipleBranches: branches.length > 1,
      reloadBranches,
      receiptBrand,
    }),
    [branches, activeBranch, activeBranchId, setActiveBranchId, reloadBranches, receiptBrand],
  );

  return (
    <BranchContext.Provider value={value}>{children}</BranchContext.Provider>
  );
}

export function useBranch(): BranchContextValue {
  const ctx = useContext(BranchContext);
  if (!ctx) {
    throw new Error("useBranch must be used within BranchProvider");
  }
  return ctx;
}
